import React, { useCallback, useEffect, useState } from 'react';
import AppHeader from './AppHeader';
import PerksEditor from './PerksEditor';
import { getProducts, createProduct, updateProduct } from '../api';
import { formatPerkLabel, parseEntitlements } from '../utils/entitlements';

const EMPTY_PRODUCT = {
  code: '',
  name: '',
  description: '',
  price: '',
  createsSeasonPass: false,
  requiresVisitDate: false,
  active: true,
  category: '',
  entitlements: [],
};

function Products({ user, onLogout, onNavigate }) {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [editing, setEditing] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [newProduct, setNewProduct] = useState(EMPTY_PRODUCT);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await getProducts({ all: true });
      setProducts(data.products || []);
    } catch (err) {
      setError(err.error || 'Unable to load products');
      setProducts([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleEditChange = (e) => {
    const { name, value, type, checked } = e.target;
    setEditing((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleNewChange = (e) => {
    const { name, value, type, checked } = e.target;
    setNewProduct((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSaveEdit = async (e) => {
    e.preventDefault();
    if (!editing) return;
    setSaving(true);
    setError('');
    setMessage('');
    try {
      await updateProduct(editing.code, {
        name: editing.name.trim(),
        description: editing.description.trim(),
        category: editing.category.trim(),
        price: Number(editing.price) || 0,
        createsSeasonPass: Boolean(editing.createsSeasonPass),
        requiresVisitDate: Boolean(editing.requiresVisitDate),
        active: Boolean(editing.active),
        entitlements: parseEntitlements(editing.entitlements || []),
      });
      setMessage(`Updated ${editing.name || editing.code}`);
      setEditing(null);
      await load();
    } catch (err) {
      setError(err.error || 'Unable to update product');
    } finally {
      setSaving(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setMessage('');
    try {
      await createProduct({
        code: newProduct.code.trim(),
        name: newProduct.name.trim(),
        description: newProduct.description.trim(),
        category: newProduct.category.trim(),
        price: Number(newProduct.price) || 0,
        createsSeasonPass: Boolean(newProduct.createsSeasonPass),
        requiresVisitDate: Boolean(newProduct.requiresVisitDate),
        active: Boolean(newProduct.active),
        entitlements: parseEntitlements(newProduct.entitlements || []),
      });
      setMessage('Product created');
      setNewProduct(EMPTY_PRODUCT);
      setShowAdd(false);
      await load();
    } catch (err) {
      setError(err.error || 'Unable to create product');
    } finally {
      setSaving(false);
    }
  };

  const productFormFields = (values, onChange, setValues, { includeCode, idPrefix }) => (
    <>
      {includeCode && (
        <div className="form-group">
          <label htmlFor={`${idPrefix}-code`}>Product code *</label>
          <input
            id={`${idPrefix}-code`}
            name="code"
            value={values.code}
            onChange={onChange}
            placeholder="e.g. daily_any"
            required
          />
        </div>
      )}
      <div className="form-group">
        <label htmlFor={`${idPrefix}-name`}>Name *</label>
        <input
          id={`${idPrefix}-name`}
          name="name"
          value={values.name}
          onChange={onChange}
          required
        />
      </div>
      <div className="form-group">
        <label htmlFor={`${idPrefix}-description`}>Description</label>
        <textarea
          id={`${idPrefix}-description`}
          name="description"
          value={values.description}
          onChange={onChange}
          rows={3}
        />
      </div>
      <div className="form-row">
        <div className="form-group">
          <label htmlFor={`${idPrefix}-price`}>Price *</label>
          <input
            id={`${idPrefix}-price`}
            name="price"
            type="number"
            min="0"
            step="0.01"
            value={values.price}
            onChange={onChange}
            required
          />
        </div>
        <div className="form-group">
          <label htmlFor={`${idPrefix}-category`}>Category</label>
          <input
            id={`${idPrefix}-category`}
            name="category"
            value={values.category}
            onChange={onChange}
            placeholder="e.g. Daily Tickets & Enchanted Passes"
          />
        </div>
      </div>
      <PerksEditor
        idPrefix={`${idPrefix}-perk`}
        perks={values.entitlements || []}
        onChange={(entitlements) => setValues((prev) => ({ ...prev, entitlements }))}
      />
      <div className="form-group">
        <label>
          <input
            type="checkbox"
            name="createsSeasonPass"
            checked={Boolean(values.createsSeasonPass)}
            onChange={onChange}
          />{' '}
          Season pass (capture holder details on order)
        </label>
      </div>
      <div className="form-group">
        <label>
          <input
            type="checkbox"
            name="requiresVisitDate"
            checked={Boolean(values.requiresVisitDate)}
            onChange={onChange}
          />{' '}
          Select-your-day ticket (guest picks visit day; usable only that day)
        </label>
      </div>
      <div className="form-group">
        <label>
          <input
            type="checkbox"
            name="active"
            checked={Boolean(values.active)}
            onChange={onChange}
          />{' '}
          Active (available for sale)
        </label>
      </div>
    </>
  );

  return (
    <div className="container wide-container">
      <AppHeader
        user={user}
        pageTitle="Products"
        subtitle="Park products for sale — category, price, perks, and active status"
        onNavigate={onNavigate}
        onLogout={onLogout}
        active="products"
      />

      {error && <div className="error">{error}</div>}
      {message && <div className="success">{message}</div>}

      <div className="section-header" style={{ marginTop: 16 }}>
        <h2>Catalog</h2>
        <button type="button" className="btn-primary" onClick={() => setShowAdd(true)}>
          + Add product
        </button>
      </div>

      {loading ? (
        <p className="subtitle">Loading products…</p>
      ) : (
        <div className="table-scroll">
          <table className="users-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Category</th>
                <th>Perks</th>
                <th>Season pass</th>
                <th>Select day</th>
                <th>Price</th>
                <th>Active</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {products.length === 0 ? (
                <tr>
                  <td colSpan={8} className="subtitle" style={{ textAlign: 'center' }}>
                    No products yet.
                  </td>
                </tr>
              ) : (
                products.map((product) => (
                  <tr
                    key={product.code}
                    className={product.active ? '' : 'compensation-row-inactive'}
                  >
                    <td>
                      <strong>{product.name}</strong>
                      <div className="subtitle" style={{ margin: 0 }}>
                        {product.code}
                      </div>
                    </td>
                    <td>{product.category || '—'}</td>
                    <td>
                      {(product.entitlements || []).length
                        ? (product.entitlements || []).map(formatPerkLabel).join('; ')
                        : '—'}
                    </td>
                    <td>{product.createsSeasonPass ? 'Yes' : 'No'}</td>
                    <td>{product.requiresVisitDate ? 'Yes' : 'No'}</td>
                    <td>${Number(product.price || 0).toFixed(2)}</td>
                    <td>{product.active ? 'Yes' : 'No'}</td>
                    <td>
                      <button
                        type="button"
                        className="btn-table"
                        onClick={() =>
                          setEditing({
                            code: product.code,
                            name: product.name || '',
                            description: product.description || '',
                            category: product.category || '',
                            price: product.price,
                            createsSeasonPass: Boolean(product.createsSeasonPass),
                            requiresVisitDate: Boolean(product.requiresVisitDate),
                            active: Boolean(product.active),
                            entitlements: parseEntitlements(product.entitlements || []),
                          })
                        }
                      >
                        Edit
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <div className="modal-overlay">
          <div className="modal-content modal-content-wide">
            <div className="modal-header">
              <h2>Edit {editing.code}</h2>
              <button type="button" className="modal-close" onClick={() => setEditing(null)}>
                ×
              </button>
            </div>
            <form onSubmit={handleSaveEdit}>
              {productFormFields(editing, handleEditChange, setEditing, {
                includeCode: false,
                idPrefix: 'edit',
              })}
              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={() => setEditing(null)}>
                  Cancel
                </button>
                <button type="submit" disabled={saving}>
                  Save changes
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showAdd && (
        <div className="modal-overlay">
          <div className="modal-content modal-content-wide">
            <div className="modal-header">
              <h2>Add product</h2>
              <button type="button" className="modal-close" onClick={() => setShowAdd(false)}>
                ×
              </button>
            </div>
            <form onSubmit={handleCreate}>
              {productFormFields(newProduct, handleNewChange, setNewProduct, {
                includeCode: true,
                idPrefix: 'new',
              })}
              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={() => setShowAdd(false)}>
                  Cancel
                </button>
                <button type="submit" disabled={saving}>
                  Create product
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default Products;
